import type {
  AttachComplianceDocumentInput,
  ComplianceDocumentContentType,
  CreateDriverInput,
  DriverAvailability,
  DriverAvailabilityHistoryEntry,
  DriverAvailabilityHistoryQuery,
  DriverAvailabilityStatus,
  DriverListQuery,
  DriverStatusChangeInput,
  EmploymentStatus,
  Page,
  PresignComplianceDocumentInput,
  TruckComplianceStatus,
  UpdateDriverInput,
} from '@chukua/common';
import { api } from '../../shared/shared.api';
import {
  type ComplianceDocumentView,
  type PresignedUpload,
  uploadToPresignedUrl,
} from '../../shared/features/compliance-documents/policies/compliance-documents';
import { toListParams } from '../../shared/policies/list-params';

export interface DriverListItem {
  id: string;
  name: string;
  phone: string;
  licenceNumber: string;
  employmentStatus: EmploymentStatus;
  availability: DriverAvailabilityStatus;
  availabilitySince: string;
  compliance: TruckComplianceStatus;
  createdAt: string;
}

export interface DriverView {
  id: string;
  name: string;
  phone: string;
  nationalId: string;
  dateOfBirth: string;
  licenceNumber: string;
  licenceClasses: string[];
  hazmatCertified: boolean;
  employmentStatus: EmploymentStatus;
  employmentStatusEffectiveAt: string;
  createdAt: string;
  updatedAt: string;
}

/** Not built yet (M6/M5) — shown as unavailable rather than a guessed shape. */
export interface UnavailablePlaceholder {
  available: false;
}

export interface DriverDetail extends DriverView {
  documents: ComplianceDocumentView[];
  advanceBalance: UnavailablePlaceholder;
  behaviourScore: UnavailablePlaceholder;
  availability: DriverAvailability;
}

export function listDrivers(query: DriverListQuery): Promise<Page<DriverListItem>> {
  return api<Page<DriverListItem>>(`/drivers?${toListParams(query)}`);
}

export async function createDriver(input: CreateDriverInput): Promise<void> {
  await api<void>('/drivers', { method: 'POST', body: JSON.stringify(input) });
}

export function getDriver(id: string): Promise<DriverDetail> {
  return api<DriverDetail>(`/drivers/${id}`);
}

export async function updateDriver(id: string, input: UpdateDriverInput): Promise<void> {
  await api<void>(`/drivers/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export async function changeDriverStatus(id: string, input: DriverStatusChangeInput): Promise<void> {
  await api<void>(`/drivers/${id}/status`, { method: 'POST', body: JSON.stringify(input) });
}

export function presignDriverDocument(driverId: string, input: PresignComplianceDocumentInput): Promise<PresignedUpload> {
  return api<PresignedUpload>(`/drivers/${driverId}/documents/presign`, { method: 'POST', body: JSON.stringify(input) });
}

/** Presign then upload in one call, so a hook only awaits the result — the key and content type it wrote to. */
export async function uploadDriverDocumentFile(driverId: string, file: File): Promise<{ key: string; contentType: ComplianceDocumentContentType }> {
  const contentType = file.type as ComplianceDocumentContentType;
  const upload = await presignDriverDocument(driverId, { contentType });
  await uploadToPresignedUrl(upload, file);
  return { key: upload.key, contentType };
}

/** Add or renew — the server resolves which by whether a current document already exists for this type. Callers reload the driver detail afterward. */
export async function attachDriverDocument(driverId: string, input: AttachComplianceDocumentInput): Promise<void> {
  await api<void>(`/drivers/${driverId}/documents`, { method: 'POST', body: JSON.stringify(input) });
}

export function getDriverDocumentDownloadUrl(driverId: string, documentId: string): Promise<{ url: string }> {
  return api<{ url: string }>(`/drivers/${driverId}/documents/${documentId}/download-url`);
}

export function listDriverAvailabilityHistory(driverId: string, query: DriverAvailabilityHistoryQuery): Promise<Page<DriverAvailabilityHistoryEntry>> {
  return api<Page<DriverAvailabilityHistoryEntry>>(`/drivers/${driverId}/availability-history?${toListParams(query)}`);
}
